# Conspiracy by Categories

import { ConspiracyEntry } from "./conspiracy-entry";

<Section index="01" title="What is a conspiracy?">

Conspiracy is defined as a [secret agreement to carry out a wrongful scheme](https://www.merriam-webster.com/dictionary/conspiracy).

I will show why this is not a constructive lens for evaluating complex systems.
It invites appeals to authority: “It wasn't secret because our trusted president
was in the meeting.” It requires proof of individual knowledge: “You cannot prove
Monsanto knew that pesticides harmful to crops and animals could also harm humans.”
Lastly, it requires knowing individual intent: “If COVID leaked from a Wuhan lab,
was it intentional?” The discussion gets stuck on intent and never reaches the
consequential outcomes.

There is also a kind of semantic paradox: if “secret” is treated as meaning
“still hidden,” a conspiracy seems to stop fitting the definition as soon as
it is proven and exposed. This enables the fallacious argument: “That's not a
secret, and therefore not a conspiracy.”

</Section>

<Section index="02" title="Theories organized by institutions">

**✓** = Verified · **?** = Questions · **$** = Financial beneficiary,

### Government

<ul className="conspiracy-list">

<ConspiracyEntry name="Tuskegee syphilis study" marks="✓" href="https://www.cdc.gov/tuskegee/about/timeline.html">

**Tuskegee, Alabama, 1932–1972:** public-health researchers deceived Black men and withheld effective treatment while studying untreated syphilis.

**Year / period:** [1932–1972](https://www.cdc.gov/tuskegee/about/timeline.html) — study period.

</ConspiracyEntry>

<ConspiracyEntry name="St. Louis aerosol tests" marks="✓" href="https://www.ncbi.nlm.nih.gov/books/NBK233549/">

**St. Louis aerosol tests, 1950s–1960s:** the Army secretly dispersed **zinc cadmium sulfide** to study how airborne biological-warfare agents could spread. Residents were exposed without knowing about the tests. The spraying itself is documented. [National Research Council](https://www.ncbi.nlm.nih.gov/books/NBK233549/)

**Year / period:** [1950s–1960s](https://www.ncbi.nlm.nih.gov/books/NBK233549/) — Army aerosol tests.

</ConspiracyEntry>

<ConspiracyEntry name="MKULTRA" marks="✓" href="https://www.intelligence.senate.gov/wp-content/uploads/2024/08/sites-default-files-hearings-95mkultra.pdf">

The CIA secretly tested drugs on unwitting people as part of its behavioral research program. In a separate Harvard study, [Ted Kaczynski, the Unabomber, underwent stressful psychological experiments directed by Henry Murray](https://www.thecrimson.com/article/2000/7/14/murray-center-seals-kaczynski-data-plondon-buried/). In *Chaos*, [Tom O'Neill explores possible links between Charles Manson and CIA-funded mind-control research](https://current.fas.harvard.edu/stories/how-psychedelics-link-1960s-liberation-laboratories).

**Year / period:** [1953](https://www.cia.gov/readingroom/document/cia-rdp78-04913a000100030118-2) — program approved.

</ConspiracyEntry>

<ConspiracyEntry name="CIA uses music industry" marks="✓" videoHref="https://www.youtube.com/watch?v=XPPc8OVNngg&t=8645s">

The CIA covertly funded the [Congress for Cultural Freedom](https://www.cia.gov/resources/csi/studies-in-intelligence/volume-61-no-3/intelligence-officers-bookshelf), which sponsored concerts to promote Western cultural ideals and counter Soviet influence. The agency also [subsidized European tours by the Boston Symphony Orchestra](https://www.cia.gov/resources/csi/static/The-Cultural-Cold-War.pdf#page=2). Its sponsorship of the Congress for Cultural Freedom was [publicly exposed in 1967](https://www.cia.gov/resources/csi/studies-in-intelligence/archives/vol-38-no-5/origins-of-the-congress-for-cultural-freedom-1949-1950/).

**Year / period:** [1950–1967](https://www.cia.gov/resources/csi/studies-in-intelligence/archives/vol-38-no-5/origins-of-the-congress-for-cultural-freedom-1949-1950/) — founding to public exposure.

</ConspiracyEntry>

<ConspiracyEntry name="Hillary Clinton emails" marks="? $" href="https://www.justice.gov/archives/opa/pr/statement-attorney-general-loretta-e-lynch-regarding-state-department-email-investigation">

The Justice Department closed its investigation into Hillary Clinton's private email system without charges in 2016.

Hillary Clinton: user-proposed beneficiary; financial gain unverified.

**Year / period:** [2016](https://www.justice.gov/archives/opa/pr/statement-attorney-general-loretta-e-lynch-regarding-state-department-email-investigation) — investigation closed.

</ConspiracyEntry>

<ConspiracyEntry name="Palmbeach Pete / Epstein death" marks="?" href="https://oig.justice.gov/sites/default/files/reports/23-085.pdf">

Epstein died amid missed guard checks, apparent sleeping, and recording failures; the medical examiner ruled his death a suicide.

**Year / period:** [2019](https://oig.justice.gov/sites/default/files/reports/23-085.pdf) — Epstein's death.

</ConspiracyEntry>

<ConspiracyEntry name="WEF (“WAF”)" marks="✓" href="https://fullfact.org/online/world-economic-forum-cashless-15-minute-central-bank/">

A 2016 WEF video presented “own nothing and be happy” as a prediction about 2030.

**Year / period:** [2016](https://fullfact.org/online/world-economic-forum-cashless-15-minute-central-bank/) — prediction published; 2030 — the scenario's future year.

</ConspiracyEntry>

<ConspiracyEntry name="COVID" marks="$" audio={<Asset id="covid-audio" />}>

The communication supression. Legal terms altered "vaccine" to include RNA technology, allowing patents and liability projection and market demand.

**Year / period:** [2020](https://www.who.int/emergencies/diseases/novel-coronavirus-2019/events-as-they-happen) — WHO characterized COVID-19 as a pandemic.

</ConspiracyEntry>

<ConspiracyEntry name="Fluoride" marks="?" videoHref="https://www.youtube.com/watch?v=986rHqZHbew">

Authorities promote [adding fluoride to public drinking water at 0.7 mg/L to prevent tooth decay and reduce oral-health disparities](https://www.cdc.gov/fluoridation/about/index.html), including in low-income communities. Research associates [higher fluoride exposures, such as drinking-water levels above 1.5 mg/L, with lower IQ in children](https://ntp.niehs.nih.gov/research/assessments/noncancer/completed/fluoride). [Diets high in free sugars increase tooth-decay risk](https://www.who.int/publications/i/item/B09443), and [decay also occurs in wild animals](https://pmc.ncbi.nlm.nih.gov/articles/PMC6117023/).

**Year / period:** [1945](https://www.cdc.gov/fluoridation/timeline-for-community-water-fluoridation/index.html) — U.S. community-water fluoridation began.

</ConspiracyEntry>

<ConspiracyEntry name="Weather control" marks="✓" videoHref="https://www.youtube.com/watch?v=BIpeNs5OWbo">

A rocket-test video is presented as evidence that NASA can create clouds and local rainfall.

**Year / period:** [2001](https://www.films.com/ecTitleDetail.aspx?TitleID=192711) — BBC's Speed episode featuring the rocket test.

</ConspiracyEntry>

<ConspiracyEntry name="Denver Airport" marks="?">

Powerful elites allegedly use secret facilities beneath Denver Airport to prepare for a global catastrophe.

**Year / period:** [1995](https://www.flydenver.com/press-release/den-celebrates-30th-anniversary/) — airport opened.

</ConspiracyEntry>

<ConspiracyEntry name="Roswell" marks="?">

The government allegedly concealed an extraterrestrial crash and alien bodies recovered near Roswell.

**Year / period:** [1947](https://www.osi.af.mil/News/Features/Display/Article/2302429/project-blue-book-part-1-ufo-reports/) — Roswell incident.

</ConspiracyEntry>

<ConspiracyEntry name="Battle of Los Angeles" marks="?">

Authorities allegedly concealed an alien encounter behind the wartime gunfire over Los Angeles.

**Year / period:** [1942](https://www.history.navy.mil/content/history/nhhc/about-us/leadership/director/directors-corner/h-grams/h-gram-010/h-010-6.html) — wartime barrage over Los Angeles.

</ConspiracyEntry>

<ConspiracyEntry name="Chemtrails" marks="?">

Aircraft allegedly spray undisclosed chemicals to manipulate weather, health, or behavior.

**Year / period:** [1990s](https://apnews.com/article/e7562efe0251588978153cef30034e63) — modern conspiracy theory emerged.

</ConspiracyEntry>

<ConspiracyEntry name="WTC" marks="✓ $">

World Trade Center Building 7 was  demolished deliberately.

July 2001. Larry Silverstein’s group had insurance coverage agreements started on June 7 and closed on July 24, 2001—49 days before September 11.

**Year / period:** 2001 — insurance agreements and September 11 attacks.

</ConspiracyEntry>

<ConspiracyEntry name="Moon-landing hoax / Buzz Aldrin" marks="?">

A disputed reading of Buzz Aldrin's interview is used to claim that the Moon landings were staged.

**Year / period:** [1969–1972](https://science.nasa.gov/resource/apollo-landing-sites-with-moon-phases/) — Apollo Moon landings.

</ConspiracyEntry>

</ul>

### Corporate

<ul className="conspiracy-list">

<ConspiracyEntry name="Monsanto" marks="$" videoHref="https://www.youtube.com/watch?v=CxVXvFOPIyQ">

Monsanto used patented seeds and restrictive licensing to make farmers dependent on its products and dominate agriculture.

Monsanto: seed sales and licensing revenue ([patent case](https://www.supremecourt.gov/opinions/boundvolumes/569bv.pdf))

**Year / period:** [1901](https://digital.sciencehistory.org/works/r207tp91k) — Monsanto founded.

</ConspiracyEntry>

<ConspiracyEntry name="Gay frogs / atrazine and Syngenta" marks="$" videoHref="https://www.youtube.com/watch?v=i5uSbp0YDhc&list=PLEBBBt_-FpQ2yT8TG-9SWcz_SHMqmXiwa">

Syngenta suppressed research linking atrazine to changes in frogs' sexual development to protect herbicide sales.

Syngenta: atrazine sales

**Year / period:** [2002](https://pubmed.ncbi.nlm.nih.gov/12410298/) and [2010](https://pubmed.ncbi.nlm.nih.gov/20194757/) — key atrazine frog studies published.

</ConspiracyEntry>

<ConspiracyEntry name="Bovine biotechnology, ticks, and Lyme" marks="?">

The working theory links cattle biotechnology, tick engineering, Lyme disease, and alpha-gal syndrome through an unresolved connection.

**Year / period:** [1975](https://www.cdc.gov/mmwr/preview/mmwrhtml/00000338.htm) — Lyme disease identified; [1993](https://www.fda.gov/animal-veterinary/product-safety-information/bovine-somatotropin-bst) — bovine somatotropin approved by the FDA; [2009](https://pmc.ncbi.nlm.nih.gov/articles/PMC3324851/) — delayed red-meat allergy and alpha-gal study published.

</ConspiracyEntry>

<ConspiracyEntry name="The Great Reset" marks="?">

Global elites allegedly used pandemic recovery plans to concentrate wealth and political control.

**Year / period:** [2020](https://www.weforum.org/press/2020/06/the-great-reset-a-unique-twin-summit-to-begin-2021/) — WEF initiative launched.

</ConspiracyEntry>

<ConspiracyEntry name="Suppressed cancer cure" marks="? $">

Pharmaceutical companies allegedly hide a universal cancer cure to preserve income from continuing treatment.

Pharmaceutical manufacturers: treatment sales

**Year / period:** Undated — no specific cure or alleged suppression event is identified.

</ConspiracyEntry>

<ConspiracyEntry name="Apple Watch conversation listening" marks="✓ $" href="https://lifehacker.com/tech/the-new-apple-watch-listens-to-your-conversations">

Apple announced Live Rewind and Siri Recap, which listen to conversations to produce text snippets and AI summaries; [Lifehacker](https://lifehacker.com/tech/the-new-apple-watch-listens-to-your-conversations) raises concerns about people nearby being included without their consent. [Apple says](https://support.apple.com/en-us/148354) users must opt in and raw audio is processed in isolated hardware, then deleted.

Apple: hardware sales.

**Year / period:** [2026](https://www.apple.com/newsroom/2026/09/introducing-apple-watch-series-12-with-the-all-new-health-sensing-system/) — announced September 9; Live Rewind and Siri Recap beta planned for late 2026.

</ConspiracyEntry>

</ul>

### Marketing

<ul className="conspiracy-list">

<ConspiracyEntry name="Kellogg's: factory-worker breakfasts and attentiveness claims" marks="$">

Kellogg's allegedly increased cereal sales by shaping workers' breakfast habits and overstating benefits for children's attention.

Kellogg's: cereal sales ([FTC case](https://www.ftc.gov/legal-library/browse/cases-proceedings/082-3145-kellogg-company-matter))

**Year / period:** [1906](https://www.wkkellogg.com/content/dam/wkkellogg-migration/who-we-are/our-story/Creating%20Joy%20and%20Connection%20Since%201906%20Booklet_Final.pdf) — company founded; [2009](https://www.ftc.gov/news-events/news/press-releases/2009/07/ftc-approves-final-consent-order-matter-concerning-kellogg-company) — FTC attentiveness-claims settlement.

</ConspiracyEntry>

<ConspiracyEntry name="Dental-hygiene marketing: twice-daily brushing and mouthwash" marks="✓ $">

Toothpaste and mouthwash makers allegedly used medical endorsements and fears of bad breath to turn dental routines into repeat purchases.

Toothpaste and mouthwash manufacturers: repeat sales

**Year / period:** [1920s](https://americanhistory.si.edu/pt/collections/object-groups/health-hygiene-and-beauty/alleviating-body-odors) — Listerine's halitosis advertising campaign.

</ConspiracyEntry>

<ConspiracyEntry name="Dietary guidance: food pyramid, breakfast promotion, and bacon-and-eggs endorsements" marks="✓ $">

Food companies allegedly used dietary guidance and physician endorsements to present profitable breakfast foods as essential to health.

Cereal, meat, and other promoted food producers: sales

**Year / period:** [1920s](https://www.eater.com/ad/17017276/egg-bacon-breakfast-science-public-relations) — Bernays's bacon-and-eggs promotion; [1992](https://archivesspace.nal.usda.gov/repositories/4/archival_objects/4000) — USDA Food Guide Pyramid.

</ConspiracyEntry>

<ConspiracyEntry name="Diamond engagement rings" marks="✓ $" href="https://www.debeersgroup.com/about-us/our-history">

De Beers promoted diamonds as symbols of lasting love through its A Diamond Is Forever advertising campaign.

De Beers and diamond retailers: diamond sales

**Year / period:** [1947](https://www.debeersgroup.com/about-us/our-history) — A Diamond Is Forever slogan created.

</ConspiracyEntry>

<ConspiracyEntry name="Sugar-industry research influence" marks="$" href="https://pubmed.ncbi.nlm.nih.gov/27617709/">

Sugar companies allegedly steered nutrition research toward blaming fat while minimizing concerns about sugar.

Sugar producers: product demand

**Year / period:** [1965–1967](https://pubmed.ncbi.nlm.nih.gov/27617709/) — sponsored research initiated and published.

</ConspiracyEntry>

</ul>

### Secret Society

<ul className="conspiracy-list">

<ConspiracyEntry name="Illuminati" marks="?">

A hidden elite network allegedly directs major institutions and world events from behind the scenes.

**Year / period:** [1776](https://www.lmu.de/en/about-lmu/lmu-at-a-glance/history/1776/) — Bavarian Illuminati founded.

</ConspiracyEntry>

<ConspiracyEntry name="Skull and Bones" marks="✓">

Yale's secret society creates private loyalties.

**Year / period:** [1832](https://www.yalealumnimagazine.com/articles/4072) — society founded.

</ConspiracyEntry>

<ConspiracyEntry name="Bohemian Grove" marks="✓">

Powerful attendees allegedly use private gatherings at Bohemian Grove to coordinate.

**Year / period:** [1872](https://www.reaganlibrary.gov/public/digitallibrary/smof/dcos/deaver/box-009/40-137-7065105-009-018-2016.pdf) — Bohemian Club founded; [1878](https://www.nps.gov/parkhistory/online_books/muwo/hrs.pdf) — summer encampment tradition began.

</ConspiracyEntry>

<ConspiracyEntry name="Presidents as puppets" marks="$">

Elected leaders serve hidden and public controllers whose interests override public accountability.

**Year / period:** Undated — no specific presidency or alleged control event is identified.

</ConspiracyEntry>

</ul>

### Education System

<ul className="conspiracy-list">

<ConspiracyEntry name="Alternative histories" marks="?">

Atlantis, The Sea Peoples, Pharaoh Akhenaten, Phoenician colonization, Admiral Byrd's expedition, Ancient Aliens, Agartha, Jules Verne, The Original Bible, and Civilizations before humans.

**Year / period:**

- Atlantis: [c. 360 BCE](https://classics.mit.edu/Plato/critias.html) — Plato's account.
- The Sea Peoples: [c. 1200 BCE](https://resources.metmuseum.org/resources/metpublications/pdf/Assyria_to_Iberia_Art_and_Culture_in_the_Iron_Age.pdf) — invasions and upheaval.
- Pharaoh Akhenaten: [c. 1353–1336 BCE](https://www.metmuseum.org/de/essays/art-architecture-and-the-city-in-the-reign-of-amenhotep-iv-akhenaten-ca-13531336-b-c) — reign.
- Phoenician colonization: [9th century BCE](https://82nd-and-fifth.metmuseum.org/toah/ht/04/afw.html) — founding of Carthage.
- Admiral Byrd's expedition: [1946–1947](https://www.history.navy.mil/browse-by-topic/exploration-and-innovation/polar-exploration0.html) — Operation Highjump.
- Ancient Aliens: [1968](https://www.everand.com/book/769201197/Chariots-of-the-Gods-50th-Anniversary-Edition) — Chariots of the Gods published.
- Agartha: [1886](https://www.innertraditions.com/books/the-kingdom-of-agarttha) — Saint-Yves's Mission de l'Inde.
- Jules Verne: [1864](https://guides.loc.gov/hollow-earth/books) — Journey to the Center of the Earth published.
- The Original Bible: undated — no specific text or alteration is identified.
- Civilizations before humans: [2018](https://arxiv.org/abs/1804.03748) — Silurian hypothesis paper published.

</ConspiracyEntry>

<ConspiracyEntry name="Textbook propaganda" marks="?">

Read China's history books on WW2.

**Year / period:** [1939–1945](https://wmr.iwm.org.uk/war/second-world-war-1939-1945) — World War II, the period discussed in the textbooks.

</ConspiracyEntry>

<ConspiracyEntry name="Maxwell Curriculum reform" marks="✓">

Robert Maxwell, [father of Epstein's former girlfriend Ghislaine Maxwell](https://apnews.com/article/ghislaine-maxwell-interview-trump-epstein-justice-department-f25291a048df5b7267254f859965d904), combined Macmillan's school-publishing business with McGraw-Hill in [1989](https://www.edweek.org/education/mcgraw-hill-macmillan-agree-to-combine-precollegiate-units/1989/05). The partnership began during the U.S. education reforms marked by the [1989 education summit and the 1990 National Education Goals](https://files.eric.ed.gov/fulltext/ED319143.pdf).

**Year / period:** [1989–1993](https://www.publishersweekly.com/pw/by-topic/industry-news/publisher-news/article/3303-macmillan-rises-from-the-dustbin.html) — publishing joint venture, ending with McGraw-Hill's acquisition of full ownership.

</ConspiracyEntry>

<ConspiracyEntry name="Göbekli Tepe" marks="✓" videoHref="https://www.youtube.com/watch?v=2BXsMgp8KLc">

In 2020, Göbekli Tepe's archaeologists [announced that conservation would take priority over expanding excavation, with future digs limited in scope](https://www.aa.com.tr/en/culture/turkey-conservation-not-excavation-focus-in-gobeklitepe/1758455). [Excavations continued in 2025](https://www.aa.com.tr/en/turkiye/2025-dig-season-concludes-at-famed-ancient-turkish-site-gobeklitepe/3709721).

**Year / period:** [c. 9600–8200 BCE](https://whc.unesco.org/en/list/1572/) — site occupation; [1995](https://www.dainst.org/en/research/projects/noslug/5746) — excavations began.

</ConspiracyEntry>

<ConspiracyEntry name="Lost technology" marks="?">

Greek Fire, Incan Airplanes, Tesla's lost technology, Piri Reis map, and Antikythera mechanism.

**Year / period:**

- Greek Fire: [7th century CE](https://gladius.revistas.csic.es/index.php/gladius/article/download/171/172) — Byzantine weapon introduced.
- Incan Airplanes: undated — specific artifacts need identification before assigning a date.
- Tesla's lost technology: [1943](https://archives.fbi.gov/archives/news/stories/2008/july/myths_072408) — Tesla died and his papers were temporarily seized.
- Piri Reis map: [1513](https://www.unesco.org/en/memory-world/piri-reis-world-map-1513?hub=66369) — map created.
- Antikythera mechanism: [c. 150–100 BCE](https://antikythera-mechanism.namuseum.gr/en/science-historian/) — mechanism created.

</ConspiracyEntry>

</ul>

### Honorable mentions

<ul className="conspiracy-list">

<ConspiracyEntry name="Titanic" marks="?" audio={<Asset id="titanic-audio" />}>

Titanic was deliberately sunk to eliminate John Jacob Astor, Benjamin Guggenheim, and Isidor Straus as alleged opponents of the Federal Reserve. Additionally the damaged Olympic was switched with Titanic for an insurance payout linked to J. P. Morgan.

**Year / period:** 1912 — [MU episode 29.24](https://mysteriousuniverse.org/2023/06/29.24-MU-Podcast-Paul-Schatzkin/).

</ConspiracyEntry>

<ConspiracyEntry name="Human engineering" marks="✓" videoHref="https://www.youtube.com/watch?v=AcaKMu7I6vU">

Scientific elites [openly propose biologically modifying humans](https://www.youtube.com/watch?v=qP7qL1Hyblk&t=1660s); “conspiracy debunked,” the proposal is being discussed openly.

**Year / period:** [2012](https://www.smatthewliao.com/wp-content/uploads/2012/02/HEandClimateChange.htm) — human-engineering paper; [2016](https://www.worldsciencefestival.com/videos/life-in-our-image-the-ethics-of-altering-the-human-genome/) — public World Science Festival panel.

</ConspiracyEntry>

<ConspiracyEntry name="Panopticon" marks="?" href="https://mysteriousuniverse.org/2021/07/26.02-MU-Podcast-The-Panopticon/" audio={<Asset id="panopticon-audio" />}>

Chinese authorities allegedly use pervasive surveillance to discourage dissent and make people police their own behavior.

**Year / period:** [2021](https://mysteriousuniverse.org/2021/07/26.02-MU-Podcast-The-Panopticon/) — MU episode 26.02 discusses China's surveillance state.

</ConspiracyEntry>

<ConspiracyEntry name="Psyops" marks="✓" href="https://www.goarmy.com/careers-and-jobs/specialty-careers/special-ops/psychological-operations" audio={<Asset id="psyops-audio" />}>

The U.S. Army uses targeted information and media to influence foreign audiences’ beliefs, emotions, and behavior.

**Year / period:** [1918](https://www.army.mil/article/199431/100_years_of_subterfuge_the_history_of_army_psychological_operations) — U.S. Army psychological-operations origins during World War I.

</ConspiracyEntry>

</ul>

</Section>

<Section index="03" title="Case studies">

### Syngenta and atrazine

Lobbying over EPA evidence standards and costly research requirements can
sideline independent research and limit Syngenta’s liability exposure.

<Asset id="syngenta-atrazine" />

[Open diagram](/_content/posts/conspiracy-by-categories/assets/syngenta-atrazine-benefit.svg) ·
[Supporting research](#syngenta-atrazine-sources)

### COVID vaccines: rules, revenue, and research ties

Purchase contracts fund demand, PREP Act coverage limits injury liability,
and patents support licensing revenue. The proposed vaccine-definition change
remains unestablished.

<Asset id="covid-vaccine-value" />

[Open diagram](/_content/posts/conspiracy-by-categories/assets/covid-vaccine-value-map.svg) ·
[Supporting research](#covid-vaccine-value-sources)

Equity investments, research collaborations, grants, and subawards connect the
institutions below. EcoHealth's foundation grant and NIH award are separate
funding streams.

<Asset id="covid-research-connections" />

[Open research connections](/_content/posts/conspiracy-by-categories/assets/covid-research-connections.svg) ·
[Supporting research](#covid-vaccine-value-sources)

### Silverstein and the World Trade Center

The WTC’s destruction led to $4.57 billion in insurance recovery for its net lessees.
Courts later dismissed one WTC-dust cleanup claim against Silverstein-linked
entities and other defendants.
Financial benefit alone does not establish responsibility for the attacks.

<Asset id="wtc-silverstein" />

[Open diagram](/_content/posts/conspiracy-by-categories/assets/wtc-silverstein-incentives.svg) ·
[Supporting research](#wtc-silverstein-sources)

</Section>

<Section index="04" title="Strange connections">

The same people, capital, and relationships recur across institutions.

| Connection | What the relationship shows |
| --- | --- |
| Epstein → Ghislaine Maxwell → Robert Maxwell → publishing/curriculum | Criminal, family, and business relationships; a specific curriculum intervention remains unidentified. |
| Gates → Fauci → Wuhan research | Distinct foundation grants and NIH/EcoHealth subawards; the notes do not establish a shared personal investment or that the cited foundation grant funded Wuhan. |
| Peter Thiel → J.D. Vance → Donald Trump | Employment, financial backing, and political selection. |
| PayPal Mafia → technology, investment, media, and political access | An alumni network with company and investment ties, including Thiel; a known network does not establish covert coordination. |

[Relationship research](#relationship-sources) ·
[PayPal Mafia](https://foundersfund.com/team/peter-thiel/)

Connections identify routes through which influence could travel. Intent is
a further question.

</Section>

<Section index="05" title="Closing remarks">

### Some conspiracies are just guys trying to make money

Recurring sales, restricted competition, favorable research, and protection
from accountability offer financial advantages. Deliberate coordination,
ordinary incentives, opportunism, and institutional failures can produce
similar outcomes. Financial gain and the pursuit of control can reinforce
one another.

### Others seek dominion

The ambition extends from shaping purchases to shaping what people can
know, say, and do. Psychological operations, the Panopticon, surveillance,
anticipated observation, censorship, managed narratives, indoctrination,
and penalties for dissent are methods of control to examine.

When does an institution's authority over conduct become an attempt to
govern conscience and belief?

### Gadianton robbers, the beasts, and institutional capture

[Helaman 6:21–23, 38–39](https://www.churchofjesuschrist.org/study/scriptures/bofm/hel/6?lang=eng) describes secret loyalties protecting
wrongdoing and a group's capture of government. [Ether 8:22–25](https://www.churchofjesuschrist.org/study/scriptures/bofm/ether/8?lang=eng)
warns later readers about secret combinations seeking power and gain.

[Revelation 13](https://www.churchofjesuschrist.org/study/scriptures/nt/rev/13?lang=eng) describes a beast from the sea with seven heads
and ten horns, followed by a beast from the earth with two horns like a lamb
and a dragon's voice. The chapter connects deception, compelled worship,
and control over buying and selling.

I read seven as a marker of periodic completion and twelve (the ten horns
plus the two horns) as a symbol of organizational wholeness:

- Twelve tribes
- Twelve apostles
- Twelve legions of angels

I see Revelation describing complete organizational capture in the last days.

If all institutions are captured, there is no need to act “secretly.”
Conspiracies would simply be carried out openly, leaving victims without recourse.

</Section>

<Section index="06" title="Sources">

<details id="topic-sources">
<summary>Institutional sources</summary>

- [Tuskegee — CDC timeline](https://www.cdc.gov/tuskegee/about/timeline.html)
- [Tuskegee — CDC FAQ on deliberate infection](https://www.cdc.gov/tuskegee/about/index.html)
- [St. Louis aerosol tests — National Research Council](https://www.ncbi.nlm.nih.gov/books/NBK233549/)
- [MKULTRA — Senate hearing](https://www.intelligence.senate.gov/wp-content/uploads/2024/08/sites-default-files-hearings-95mkultra.pdf)
- [Hillary Clinton email investigation — Justice Department](https://www.justice.gov/archives/opa/pr/statement-attorney-general-loretta-e-lynch-regarding-state-department-email-investigation)
- [Epstein custody investigation — DOJ inspector general](https://oig.justice.gov/sites/default/files/reports/23-085.pdf)
- [COVID origins — WHO report](https://www.who.int/news/item/27-06-2025-who-scientific-advisory-group-issues-report-on-origins-of-covid-19)
- [Fluoridation — CDC](https://www.cdc.gov/fluoridation/about/index.html)
- [Atrazine frog research](https://pubmed.ncbi.nlm.nih.gov/20194757/)
- [Alpha-gal — CDC](https://www.cdc.gov/alpha-gal-syndrome/about/index.html)
- [Lyme disease — CDC](https://www.cdc.gov/lyme/about/index.html)
- [Kellogg advertising — FTC consent order](https://www.ftc.gov/news-events/news/press-releases/2009/07/ftc-approves-final-consent-order-matter-concerning-kellogg-company)
- [Home oral care — American Dental Association](https://www.ada.org/resources/ada-library/oral-health-topics/home-oral-care/)
- [Food pyramid — USDA archive](https://archivesspace.nal.usda.gov/repositories/4/archival_objects/4000)
- [De Beers company history](https://www.debeersgroup.com/about-us/our-history)
- [Sugar-industry research influence](https://pubmed.ncbi.nlm.nih.gov/27617709/)

</details>

<details id="syngenta-atrazine-sources">
<summary>Syngenta sources</summary>

1. [Rachel Aviv, “A Valuable Reputation,” The New Yorker, February 10, 2014](https://www.newyorker.com/magazine/2014/02/10/a-valuable-reputation).
   Secondary reporting used for the Syngenta–Tozzi relationship and reported
   tactics. The original note and video are research leads, not verification.
2. [CRE, Kansas Corn Growers Association, and Triazine Network petition,
   November 25, 2002, hosted by EPA](https://www.epa.gov/sites/default/files/2019-02/documents/2807.pdf). The filing establishes what the
   petitioners requested; its arguments are not agency findings.
3. [FIFRA Scientific Advisory Panel, June 12–14, 2012 meeting report](https://www.epa.gov/sites/default/files/2015-06/documents/061212minutes.pdf).
   Printed pp. 14–15 and 27–29 correspond to PDF pp. 16–17 and 29–31.
4. [EPA, archived “Atrazine Updates”](https://archive.epa.gov/pesticides/reregistration/web/html/atrazine_update.html). Historical ecological
   monitoring, mitigation, and outside data sources; not a claim about the
   status of every monitoring program today.
5. [Syngenta, 2012 Full Year Results, released February 6, 2013](https://www.syngenta.com/sites/syngenta/files/press-release-pdf/2013/20130206-en-full-year-results-2012.pdf).
   Note 5, PDF p. 20: settlement payment, denial of liability, and company rationale.
6. [EPA response to the atrazine information-quality petition, March 27, 2003](https://www.epa.gov/sites/default/files/2016-03/documents/2807Response_03_27_03.pdf).
   Printed pp. 18–20: assessment revisions and the agency’s treatment of unvalidated studies.
7. [Syngenta, “Atrazine — The Facts”](https://www.atrazine.com/fast-facts), accessed September 12, 2026.
   The company describes required industry studies as having multimillion-dollar costs.
   This is a general cost claim, not a price for the specific amphibian study;
   the diagram’s “pay to play” barrier is an interpretation of cost and evidence-selection pressures.

</details>

<details id="covid-vaccine-value-sources">
<summary>COVID vaccine sources</summary>

1. [2020 COVID PREP declaration](https://www.federalregister.gov/documents/2020/03/17/2020-05484/declaration-under-the-public-readiness-and-emergency-preparedness-act-for-medical-countermeasures), supplementary information
   (2013 amendment), sections IV–VII (coverage), and section XIV (compensation);
   [ASPR overview](https://www.aspr.gov/about/law-policy-strategy/amendments-public-health-service-act/prep-act) and [questions and answers](https://www.aspr.gov/about/law-policy-strategy/amendments-public-health-service-act/prep-act/prep-act-frequently-asked-questions/prep-act-question-answers).
2. [FDA, Emergency Use Authorization for Vaccines Explained](https://www.fda.gov/vaccines-blood-biologics/vaccines/emergency-use-authorization-vaccines-explained).
3. [DoD, July 22, 2020 Pfizer/BioNTech purchase announcement](https://www.defense.gov/News/Releases/Release/Article/2310994/us-government-engages-pfizer-to-produce-millions-of-doses-of-covid-19-vaccine/).
4. [USPTO patent requirements](https://www.uspto.gov/patents/basics/using-legal-services/pro-se-assistance-program) and [Penn patent US 8,278,036](https://patents.google.com/patent/US8278036B2/en).
5. [BioNTech 2019 F-1 licensing disclosures](https://www.sec.gov/Archives/edgar/data/1776985/000119312519241112/d635330df1.htm),
   [2023 20-F and exhibits](https://www.biontech.com/content/dam/corporate/pdf/20-F%202023.pdf), [Moderna patent list](https://www.modernatx.com/patents),
   [Acuitas history](https://acuitastx.com/our-journey/), [Genevant settlement](https://www.genevant.com/genevant-sciences-and-arbutus-biopharma-announce-2-25-billion-global-settlement-with-moderna/), and
   [spike patent US 10,960,070](https://patents.google.com/patent/US10960070B2/en).
6. [FDA licensed vaccines](https://www.fda.gov/vaccines-blood-biologics/vaccines/vaccines-licensed-use-united-states), [BioNTech product update](https://www.biontech.com/au/en/home/mediaroom/news/press-releases/2026/08/Pfizer-and-BioNTech-Receive-U-S-FDA-Approval-for-XFG-adapted-COVID-19-Vaccine.html),
   and [Sanofi/Novavax agreement](https://www.sanofi.com/en/media-room/press-releases/2024/2024-05-10-06-00-00-2879379).
7. [Novavax Q2 2024 financial results](https://ir.novavax.com/press-releases/2024-08-08-Novavax-Reports-Second-Quarter-2024-Financial-Results-and-Operational-Highlights).
8. [Genevant/Arbutus announcement, March 3, 2026](https://www.genevant.com/genevant-sciences-and-arbutus-biopharma-announce-2-25-billion-global-settlement-with-moderna/), payment and
   Section 1498 contingency paragraphs. No later outcome is asserted.
9. [BioNTech/Gates Foundation announcement, September 4, 2019](https://www.biontech.com/int/en/home/mediaroom/news/press-releases/2019/09/biontech-announces-new-collaboration-develop-hiv-and.html).
10. [NIH/Gates collaboration announcement, October 23, 2019](https://www.nih.gov/news-events/news-releases/nih-launches-new-collaboration-develop-gene-based-cures-sickle-cell-disease-hiv-global-scale).
11. [Gates Foundation grant INV002838](https://www.gatesfoundation.org/about/committed-grants/2021/03/inv002838), purpose, date,
    committed amount, and regions fields.
12. [HHS OIG audit A-05-21-00025](https://oig.hhs.gov/oas/reports/region5/52100025.pdf), award R01AI110964 and
    oversight findings; [GAO-23-106119](https://www.gao.gov/assets/830/826738.pdf) supplies additional chronology.
13. [NIAID biography of Anthony Fauci](https://www.niaid.nih.gov/about/anthony-s-fauci-md-bio).

</details>

<details id="wtc-silverstein-sources">
<summary>World Trade Center sources</summary>

1. [World Trade Center Properties v. Hartford Fire Insurance, 345 F.3d 154
   (2d Cir. 2003)](https://law.justia.com/cases/federal/appellate-courts/F3/345/154/550575/). Background and the policy-form/occurrence analysis.
2. [Port Authority, 2011 financial statements](https://www.panynj.gov/content/dam/corporate/financial-statements/financial-statement-2011.pdf). Note K, printed p. 67
   / PDF p. 70: net-lessee insurance recoveries and uses.
3. [World Trade Center Properties v. United Airlines, document 227
   (S.D.N.Y., April 6, 2017)](https://law.justia.com/cases/federal/district-courts/new-york/nysdce/1:2008cv03719/324321/227/). Opinion pp. 15–16, quoting original lease
   sections 15.1, 15.1.4, 15.2, and 15.4; main-site lease discussion excludes WTC 7.
4. [In re September 11 Litigation / Cedar & Washington, No. 10-4197
   (2d Cir., May 2, 2014)](https://law.justia.com/cases/federal/appellate-courts/ca2/10-4197/10-4197-2014-05-02.html). Background, CERCLA defense,
   insurance-law distinction, and indemnification analysis.
5. [Cedar & Washington v. Port Authority, document 138
   (S.D.N.Y., March 20, 2013)](https://law.justia.com/cases/federal/district-courts/new-york/nysdce/1:2008cv09146/334279/138/). Act-of-war ruling and expressly
   narrow conclusion, including the warning about other claims.
6. [Port Authority v. Affiliated FM Insurance, 245 F. Supp. 2d 563
   (D.N.J., May 17, 2001)](https://law.justia.com/cases/federal/district-courts/FSupp2/245/563/2515040/). Asbestos management/removal sections and
   first-party property-coverage analysis.
7. [EPA OIG, EPA's Response to the World Trade Center Collapse,
   Report 2003-P-00012, August 21, 2003](https://www.epa.gov/sites/production/files/2015-12/documents/wtc_report_20030821.pdf). Executive summary and chapter 2:
   dust contamination and limits of early safety assurances.

</details>

<details id="relationship-sources">
<summary>Relationship sources</summary>

- [DOJ OIG Epstein custody report (June 2023)](https://oig.justice.gov/sites/default/files/reports/23-085.pdf): executive summary on Maxwell's conviction.
- [AP profile of Ghislaine Maxwell](https://apnews.com/article/ghislaine-maxwell-interview-trump-epstein-justice-department-f25291a048df5b7267254f859965d904): family and publishing background.
- [Gates Foundation grant record, INV002838](https://www.gatesfoundation.org/about/committed-grants/2021/03/inv002838): grant amount, date, recipient, and stated purpose.
- [HHS OIG NIH/EcoHealth audit (2023)](https://www.oig.hhs.gov/reports/all/2023/the-national-institutes-of-health-and-ecohealth-alliance-did-not-effectively-monitor-awards-and-subawards-resulting-in-missed-opportunities-to-oversee-research-and-other-deficiencies/): awards, subawards, and oversight findings.
- [NIAID biography of Anthony Fauci](https://www.niaid.nih.gov/about/anthony-s-fauci-md-bio): director tenure.
- [Reuters, August 20, 2024, republished by Investing.com](https://www.investing.com/news/world-news/tech-donor-network-cofounded-by-jd-vance-seeks-to-push-america-to-the-right-3578816): Vance's employment, Thiel's backing, and Trump's running-mate selection.

</details>

<details id="control-sources">
<summary>Surveillance, censorship, and psychological operations</summary>

- [The Panopticon — Mysterious Universe episode](https://mysteriousuniverse.org/2021/07/26.02-MU-Podcast-The-Panopticon/)
- [U.S. Army — Psychological Operations](https://www.goarmy.com/careers-and-jobs/specialty-careers/special-ops/psychological-operations)
- [U.S. Army — 100 years of subterfuge: the history of Army psychological operations](https://www.army.mil/article/199431/100_years_of_subterfuge_the_history_of_army_psychological_operations)
- [China Cables — documents and translations](https://www.icij.org/investigations/china-cables/read-the-china-cables-documents/)
- [Document No. 9 — English translation](https://www.chinafile.com/document-9-chinafile-translation)
- [COVID censorship — ProPublica investigation](https://www.propublica.org/article/leaked-documents-show-how-chinas-army-of-paid-internet-trolls-helped-censor-the-coronavirus)

</details>

</Section>
